import { selectAllowUpgrade } from '@va/aaas/shared/selectors';
import { TEST_IDS } from '@va/constants';
import { startUpgradeProcess } from '@va/dashboard/actions/app';
import { NavSettings } from '@va/icons';
import { useLocale, useTranslate } from '@va/localization';
import { hasPermission } from '@va/standalone/shared/selectors';
import { Heading1, Heading4, Paragraph, fontWeights } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { addNumberSeparator, is3asApp } from '@va/util/helpers';
import { useCheckOverflow } from '@va/util/hooks';
import classNames from 'classnames';
import React, { Fragment, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FeatureCardPlaceholderSkeleton from './FeatureCardPlaceholderSkeleton';
import './FeatureTrackingCard.scss';
import InactiveFeatureCard from './InactiveFeatureCard';
import { FeatureTrackingCardProps } from './featureTrackingCardPropsDefinition';

export const featureCardIconColors = {
  base: '#696969',
  disabled: '#C3C3C3',
};

export const getFeatureCardIconColor = (disabled: boolean) =>
  disabled ? featureCardIconColors.disabled : featureCardIconColors.base;

export const featureTrackingCardWrapperClasses =
  'bg-white feature-tracking-card-wrapper w-full h-full rounded-30 p-18px sm:p-3 overflow-hidden';

export const FeatureTrackingCard: React.FC<FeatureTrackingCardProps> = ({
  inProgress,
  onSettingsClicked,
  featureStatus,
  featureActions,
  featureInsights,
  featureStatistics,
  featureTitle,
  featureKey,
  isFeatureActive = true,
  onActivateClick,
}) => {
  const translate = useTranslate();
  const dispatch = useDispatch();

  const { featureIcon, activeCount, featureText, featureTooltip } = featureStatus;
  const { locale } = useLocale();
  const hasUpgradeAccountPlanPermission = useSelector(hasPermission);
  const allowIntpUpgrade = useSelector(selectAllowUpgrade);

  function handleUpgradeButtonClick() {
    if (is3asApp()) {
      dispatch(startUpgradeProcess());
      return;
    }
    if (!hasUpgradeAccountPlanPermission) return;
    dispatch(startUpgradeProcess());
  }

  const shouldShowUpgradeButton = useMemo(() => !is3asApp() || allowIntpUpgrade, [allowIntpUpgrade]);

  const totalPlaceholdersItems = (featureInsights?.length ?? 0) + (featureStatistics?.length ?? 0);

  if (inProgress) {
    return (
      <div className={featureTrackingCardWrapperClasses}>
        <FeatureCardPlaceholderSkeleton totalFeatureInfoItems={totalPlaceholdersItems} />
      </div>
    );
  }

  if (!isFeatureActive) {
    return (
      <div className={classNames(featureTrackingCardWrapperClasses, 'flex flex-col gap-4')}>
        <InactiveFeatureCard
          featureTooltip={featureTooltip}
          featureIcon={featureIcon}
          featureTitle={featureTitle ?? ''}
          featureKey={featureKey ?? ''}
          onActivateClick={onActivateClick}
        />
      </div>
    );
  }

  return (
    <div className={featureTrackingCardWrapperClasses}>
      <div className='scrollbar scrollbar-thumb flex flex-col overflow-auto h-full gap-3'>
        <div className='grow flex flex-col gap-4 xl:flex-row'>
          <div
            data-testid={TEST_IDS.generic.featureCard.statusSection}
            className={classNames(
              'w-full xl:w-1/2 grow flex flex-col justify-center items-center bg-white-snow p-4 rounded-18',
              { 'feature-status-section': !is3asApp(), 'gap-3': is3asApp() },
            )}
          >
            <div className='feature-icon mb-2'>{featureIcon()}</div>
            <div className='feature-status-content flex flex-col justify-center items-center text-center'>
              <Heading1 colorClassName='text-gray-code ' className='' weight={fontWeights.semibold}>
                {addNumberSeparator(activeCount, locale)}
              </Heading1>
              <TooltipWrapper trigger='click' content={featureTooltip} disabled={!featureTooltip}>
                <p
                  className={classNames('text-lg leading-6 text-gray-charcoal font-semibold mt-1.5 tracking-n0015', {
                    'border-b border-dashed border-gray-silver cursor-default': featureTooltip,
                  })}
                >
                  {featureText}
                </p>
              </TooltipWrapper>
            </div>
            <div className='feature-settings-content flex flex-row items-center w-full justify-center gap-4'>
              {onSettingsClicked && (
                <div
                  data-testid={TEST_IDS.generic.featureCard.settingsBtn}
                  onClick={onSettingsClicked}
                  className={classNames(
                    'w-12 h-12 shrink-0 flex justify-center items-center bg-white rounded-12 cursor-pointer hover:bg-white-smoke',
                    { 'w-full': is3asApp() },
                  )}
                >
                  <NavSettings color={'var(--color-primary)'} />
                </div>
              )}
              {shouldShowUpgradeButton && (
                <TooltipWrapper
                  interactive
                  disabled={hasUpgradeAccountPlanPermission}
                  content={translate('tooltips.contributorRole')}
                >
                  <div
                    data-testid={TEST_IDS.generic.buttons.upgrade}
                    onClick={handleUpgradeButtonClick}
                    className='flex grow items-center justify-center cursor-pointer h-12 hover:bg-white-smoke rounded-12 bg-white px-18px py-12px text-15 leading-normal text-gray-charcoal font-medium tracking-n0012'
                  >
                    {translate('text.upgradePlan')}
                  </div>
                </TooltipWrapper>
              )}
            </div>
          </div>
          <div className='w-full xl:w-1/2 grow flex flex-col gap-3'>
            {featureActions.map((item, index) => {
              if (item.defaultContent) {
                const {
                  icon: actionIcon,
                  title,
                  subTitle,
                  disabled = false,
                  tooltipText,
                  onClick,
                  sectionStyle = null,
                  ...rest
                } = item.defaultContent;
                return (
                  <TooltipWrapper key={index} interactive disabled={!tooltipText} content={tooltipText}>
                    <div
                      data-testid={rest['data-testid']}
                      className={classNames(
                        'flex-1 flex items-center justify-start p-3 gap-4 rounded-18 bg-white-snow min-h-[4.5rem] cursor-pointer text-gray-code',
                        disabled ? 'cursor-not-allowed' : '',
                        { 'hover:bg-white-smoke': !disabled && !sectionStyle },
                        sectionStyle,
                      )}
                      onClick={disabled ? () => {} : onClick}
                    >
                      <div>{actionIcon(getFeatureCardIconColor(disabled))}</div>
                      <div className={classNames({ 'text-gray-silver': disabled })}>
                        <Heading4 weight={fontWeights.semibold} colorClassName='text-inherit line-clamp-2'>
                          {title}
                        </Heading4>
                        <Paragraph weight={fontWeights.medium} colorClassName='text-inherit' className='line-clamp-2'>
                          {subTitle}
                        </Paragraph>
                      </div>
                    </div>
                  </TooltipWrapper>
                );
              } else if (item.customContent) {
                return <Fragment key={index}>{item.customContent()}</Fragment>;
              } else {
                return null;
              }
            })}
          </div>
        </div>
        {featureInsights?.map(({ label, value, tooltipText }, index) => {
          return <FeatureInsightsItem key={index} label={label} value={value} tooltipText={tooltipText} />;
        })}
        {featureStatistics?.map(({ info, icon: statsIcon }, index) => (
          <FeatureStatisticsItem key={index} info={info} statsIcon={statsIcon} />
        ))}
      </div>
    </div>
  );
};

export default FeatureTrackingCard;

const FeatureInsightsItem = ({
  label,
  value,
  tooltipText,
}: {
  label: string;
  value: number | string;
  tooltipText?: string;
}) => {
  const labelRef = useRef(null);
  const isOverflow = useCheckOverflow(labelRef);

  return (
    <div className='grow flex flex-row flex-nowrap items-center justify-between bg-white-snow px-30 py-4 rounded-18 gap-3 h-[4.5rem] max-h-[4.5rem]'>
      <TooltipWrapper
        trigger='click'
        content={
          <div className='text-center'>
            {isOverflow && <p>{label}</p>}
            {tooltipText && <p>{tooltipText}</p>}
          </div>
        }
        disabled={tooltipText ? false : !isOverflow}
      >
        <Paragraph
          ref={labelRef}
          colorClassName='text-gray-charcoal truncate'
          className={classNames({
            'border-b-1 border-dashed border-gray-silver cursor-default': tooltipText,
          })}
          weight={fontWeights.medium}
        >
          {label}
        </Paragraph>
      </TooltipWrapper>
      <Paragraph colorClassName='text-gray-charcoal whitespace-nowrap' weight={fontWeights.medium}>
        {value}
      </Paragraph>
    </div>
  );
};

const FeatureStatisticsItem = ({
  info,
  statsIcon,
}: {
  info: { label: string; tooltip?: string }[];
  statsIcon?: () => React.ReactNode;
}) => {
  const translate = useTranslate();
  return (
    <div className='w-full flex flex-col xl:flex-row gap-2 grow items-center justify-between bg-white-snow px-18px py-3 rounded-18 xl:h-[4.5rem] xl:max-h-[4.5rem]'>
      {info.map(({ label, tooltip }, index: number) => (
        <FeatureStatsInfoItem info={label} key={index} tooltipText={tooltip} />
      ))}
      {statsIcon && (
        <TooltipWrapper interactive content={translate('feature.trackingCard.viewResponse')}>
          <div className='h-12 w-12 shrink-0 flex justify-center items-center bg-white rounded-12 cursor-pointer hover:bg-white-smoke'>
            {statsIcon()}
          </div>
        </TooltipWrapper>
      )}
    </div>
  );
};

const FeatureStatsInfoItem = ({ info, tooltipText }: { info: string; tooltipText?: string }) => {
  const ref = useRef(null);
  const isOverflowing = useCheckOverflow(ref);
  return (
    <Paragraph
      colorClassName='text-gray-charcoal'
      weight={fontWeights.medium}
      className='w-full grow flex justify-center items-center bg-white px-18px py-12px rounded-12 truncate'
    >
      <TooltipWrapper
        trigger='click'
        disabled={tooltipText ? false : !isOverflowing}
        content={
          <div className='text-center'>
            {isOverflowing && <p>{info}</p>}
            {tooltipText && <p>{tooltipText}</p>}
          </div>
        }
      >
        <span
          className={classNames('truncate', {
            'border-b-1 border-dashed border-gray-silver cursor-default': tooltipText,
          })}
          ref={ref}
        >
          {info}
        </span>
      </TooltipWrapper>
    </Paragraph>
  );
};
